<script lang="ts">
	//UI
	import ComponentCover from "@hitit/hub/src/v7/components/common/ComponentCover.svelte";
	import ComponentLoading from "@hitit/hub/src/v7/components/common/ComponentLoading.svelte";
	import { AdaptiveTextInput, Card, DefaultSwitch } from "@hitit/new_ui";
	import EditIcon from "@hitit/hub/src/assets/Icons/Edit.png";
	import SaveIcon from "@hitit/hub/src/assets/Icons/Save.png";
	import { api, notifications, t } from "@hitit/hub/src/stores";
	import StoreNameValue from "@hitit/hub/src/v7/components/store/StoreNameValue.svelte";
	//Utils
	import { onlyNumberHandler } from "@hitit/hub/src/v7/utils";
	//Types
	import type { PaymentSettingDTO } from "@hitit/api";
	//Props
	export let paymentSetting: PaymentSettingDTO | null = null;
	export let coverStatus = false;
	export let editmode = false;
	export let editable = false;
	export let storeId = "";
	//Constants
	const message = "결제 설정 저장 도중 문제가 발생하였습니다."
	//Variables
	let openCashBoxWhenPaidByCashEnabled: boolean;
	let rotateScreenDuringPaymentEnabled: boolean;
	let autoClosePaymentScreenWhenDoneEnabled: boolean;
	let minimumInstallmentAmount: string;

	let loading = false;
	let error = "";

	//Functions
	async function actionHandler() {
		if (!editmode) {
			editmode = true;
			return;
		}

		try {
			if (Number(minimumInstallmentAmount) < 50000) {
				alert("할부 최소 금액은 50000원 입니다.");
				return;
			}

			// const paymentSettingDTO = {
			// 	openCashBoxWhenPaidByCashEnabled,
			// 	rotateScreenDuringPaymentEnabled,
			// 	autoClosePaymentScreenWhenDoneEnabled,
			// 	installmentTriggerPayValue: Number(minimumInstallmentAmount),
			// };
			// await $api.setPaymentSetting(storeId, paymentSettingDTO);
			alert('아직 사용할 수 없는 기능입니다. 서버에 저장되지 않습니다.')

			error = "";
			loading = true;

			editmode = false;
		} catch (error) {
			console.log(error);
			notifications.append("ERROR", message, "", undefined, "WARNING");
		} finally {
			loading = false;
		}
	}

	function cashDrawerChangeHandler(e: Event) {
		const target = e.target as HTMLInputElement;
		openCashBoxWhenPaidByCashEnabled = target.checked ? true : false;
	}
	function windowAutoCloseChangeHandler(e: Event) {
		const target = e.target as HTMLInputElement;
		autoClosePaymentScreenWhenDoneEnabled = target.checked ? true : false;
	}
	function orderHistoryScreenChangeHandler(e: Event) {
		const target = e.target as HTMLInputElement;
		rotateScreenDuringPaymentEnabled = target.checked ? true : false;
	}

	function dataConvert(paymentSetting: PaymentSettingDTO | null) {
		openCashBoxWhenPaidByCashEnabled = paymentSetting
			? paymentSetting!.openCashBoxWhenPaidByCashEnabled
			: false;
		rotateScreenDuringPaymentEnabled = paymentSetting
			? paymentSetting!.rotateScreenDuringPaymentEnabled
			: false;
		autoClosePaymentScreenWhenDoneEnabled = paymentSetting
			? paymentSetting!.autoClosePaymentScreenWhenDoneEnabled
			: false;
		minimumInstallmentAmount = String(
			paymentSetting ? paymentSetting!.installmentTriggerPayValue : 0
		);
	}

	const inputHandler = (e: Event) => {
		const target = e.currentTarget as HTMLInputElement;
		onlyNumberHandler(target, (_, v) => {
			minimumInstallmentAmount = v;
		});
	};

	//Do
	$: dataConvert(paymentSetting);
</script>

<div class="root-wrapper">
	<Card noPadding>
		<div class="container" t-id="PaymentSettingWidget">
			{#if coverStatus}
				<ComponentCover />
			{/if}
			{#if loading}
				<ComponentLoading coverMode />
			{/if}
			{#if editable}
				<div class="action" on:click={actionHandler}>
					<span>{editmode ? $t("global.done") : $t("global.edit")}</span>
					<img
						src={editmode ? SaveIcon : EditIcon}
						alt={editmode ? "save" : "edit"}
					/>
				</div>
			{/if}
			<p class="title">{$t("hub.store.payment_settings.paymentsettings")}</p>
			{#if editmode && error}
				<p class="error">{error}</p>
			{/if}
			<div class="content-wrapper">
				<div class="name-value">
					<StoreNameValue
						name={$t("hub.store.payment_settings.cash_drawer_interlocking")}
						editmode={editmode}
						value={openCashBoxWhenPaidByCashEnabled === true
							? $t("hub.store.widget.cashreceipt.enabled")
							: $t("hub.store.widget.cashreceipt.disabled")}
					>
						<div slot="input" class="lock-box">
							<DefaultSwitch
								checked={openCashBoxWhenPaidByCashEnabled === true}
								on:change={cashDrawerChangeHandler}
							/>
						</div>
					</StoreNameValue>
					<StoreNameValue
						name={$t("hub.store.payment_settings.payment_window_auto_close")}
						editmode={editmode}
						value={autoClosePaymentScreenWhenDoneEnabled === true
							? $t("hub.store.widget.cashreceipt.enabled")
							: $t("hub.store.widget.cashreceipt.disabled")}
					>
						<div slot="input" class="lock-box" t-id="auto-close">
							<DefaultSwitch
								checked={autoClosePaymentScreenWhenDoneEnabled === true}
								on:change={windowAutoCloseChangeHandler}
							/>
						</div>
					</StoreNameValue>
				</div>
				<div class="name-value">
					<StoreNameValue
						name={$t("hub.store.payment_settings.switch_to_payment_window")}
						editmode={editmode}
						value={rotateScreenDuringPaymentEnabled === true
							? $t("hub.store.widget.cashreceipt.enabled")
							: $t("hub.store.widget.cashreceipt.disabled")}
					>
						<div slot="input" class="lock-box">
							<DefaultSwitch
								checked={rotateScreenDuringPaymentEnabled === true}
								on:change={orderHistoryScreenChangeHandler}
							/>
						</div>
					</StoreNameValue>
					<StoreNameValue
						value={minimumInstallmentAmount}
						name={$t("hub.store.payment_settings.minimum_installment_amount")}
						editmode={editmode}
					>
						<div slot="input">
							<div class="input">
								<AdaptiveTextInput
									bind:value={minimumInstallmentAmount}
									backgroundColor={"#f0f0f4"}
									on:input={inputHandler}
									clearCallback={() => (minimumInstallmentAmount = "")}
								/>
							</div>
						</div>
					</StoreNameValue>
				</div>
			</div>
		</div>
	</Card>
</div>

<style lang="scss">
	@import "../../../variables.scss";
	.root-wrapper {
		width: 100%;
	}
	.container {
		width: 100%;
		display: flex;
		flex-direction: column;
		padding: 20px 10px;
		gap: 20px;
		.title {
			font-size: 1.25rem;
			font-weight: 600;
			color: #46474b;
		}
		.action {
			display: flex;
			gap: 10px;
			align-items: center;
			position: absolute;
			right: 10px;
			top: 10px;
			color: #6d6f76;
			font-weight: 500;

			&:hover {
				cursor: pointer;
			}
			img {
				width: 24px;
				height: 24px;
				object-fit: contain;
			}
		}
		.content-wrapper {
			display: flex;
			flex-direction: column;
			gap: 20px;
			.name-value {
				display: flex;
				flex-direction: column;
				width: 100%;
				max-width: 100%;
				gap: 20px;
			}
		}
	}
	@include DesktopSize {
		.root-wrapper {
			max-height: 300px;
		}
		.container {
			padding: 30px;
			gap: 30px;
			.title {
				font-size: 2.125rem;
			}
			.action {
				top: 20px;
				right: 20px;
				font-size: 1.25rem;
			}
			.content-wrapper {
				flex-direction: row;
				gap: unset;
				.name-value {
					gap: 30px;
					width: 50%;
					max-width: 50%;
				}
			}
			.input {
				height: 40px;
				width: 100%;
				max-width: 100%;
				font-size: 1.25rem;
			}
		}
	}
</style>
